import {
	Dialog,
	Form,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useState } from "react";
import { useAlerts } from "client/Hooks";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { IFormHandler, useFormHandler } from "client/Forms";
import { OAuth2ApplicationUpdateResult, updateSingleSignOn } from "client/Api/SingleSignOnSettings/singleSignOnSettings";

export type UpdateConfirmationDialogProps = {
	onSuccess: (consentUrl: string) => void;
	onFail: (reason: string) => void;
	onCancel: () => void;
};

type UpdateConfirmationFormModel = {
	password: string;
};

const validationBuilder: IValidationConfiguration<UpdateConfirmationFormModel> =
	createValidationConfiguration<UpdateConfirmationFormModel>();

validationBuilder
	.forScalar("password")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export const UpdateConfirmationDialog = (props: UpdateConfirmationDialogProps): ReactElement => {
	const {
		onCancel,
		onFail,
		onSuccess
	} = props;

	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState<UpdateConfirmationFormModel>({ password: "" });
	const { showError } = useAlerts();

	const formHandler: IFormHandler<UpdateConfirmationFormModel> = useFormHandler(
		formData,
		setFormData,
		validationBuilder
	);

	const saveHandler = async (): Promise<void> => {
		const validationResult: InputValidationResult = await formHandler.validation.validate();

		if (validationResult.kind === InputValidationResultKind.Invalid || isLoading) {
			return;
		}

		try {
			setIsLoading(true);

			const result: OAuth2ApplicationUpdateResult = await updateSingleSignOn(formData.password);

			setIsLoading(false);

			if (result.wasSuccessful) {
				onSuccess(result.adminConsentUrl);
			} else {
				onFail(result.reason);
			}
		} catch (e) {
			if (!formHandler.validation.tryProcessServerError(e)) {
				showError(localize("_GeneralErrorMessage"));
			}

			setIsLoading(false);
		}
	};

	return (
		<Dialog
			trackingId="azure-password-dialog"
			hasBusyIndicator={isLoading}
		>
			<Dialog.Header
				title={localize("_AreYouSureYouWantToUpdateTheSingleSignOnAppInAzureAd")}
				onClose={onCancel}
			/>
			<Dialog.Body>
				<p>
					<strong>{localize("_Note")}</strong>: {localize("_AfterUpdatingTheAppYourUsersMayNeedToLoginAgain")}
				</p>
				<Form onSubmit={saveHandler}>
					<TextInput
						{...formHandler.for("password").attachToTextInput()}
						trackingId="password-input"
						type="password"
						placeholder={localize("_Password")}
					/>
					<ValidationError {...formHandler.for("password").attachToValidationError()} />
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="update-sso-button"
						kind="primary"
						text={localize("_UpdateSingleSignOnApp")}
						onClick={saveHandler}
					/>
					<Toolbar.Button
						trackingId="cancel-button"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onCancel}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

UpdateConfirmationDialog.displayName = "UpdateConfirmationDialog";
